import React, { useState, useEffect, useMemo } from 'react'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { Button } from '@progress/kendo-react-buttons'
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import {
  FormNumericTextBox,
  FormInput,
  FormDropDownList,
} from '../FormComponents'
import { useToast } from '../../../contexts/ToastContext'
import { piecesLineitem, dropDown, CreatePiecesLineitemVM } from '../../../data/interfaces'
import {
  getPiecesLineitemsByPiecesId,
  createPiecesLineitem,
  updatePiecesLineitem,
  deletePiecesLineitem
} from '../../../data/piecesLineitemData'

interface PiecesLineitemManagerProps {
  piecesId: number
  xeroAccountData: dropDown[]
  xeroSupplierData: dropDown[]
  xeroDepartmentData: dropDown[]
  xeroTaxRateData: dropDown[]
  xeroTypeData: dropDown[]
  jobsData?: dropDown[]
  lockEdit?: boolean
}

interface LineitemFormData {
  piecesLineitemId: number
  projectId: number
  quantity: number
  unitPrice: number
  xeroAccountId: number
  xeroSupplierId: number
  xeroTrackingDepartmentId: number
  xeroTaxRateId: number
  xeroTrackingTypeId: number
}

const PiecesLineitemManager: React.FC<PiecesLineitemManagerProps> = (props) => {
  const { showToast } = useToast()
  const [lineItems, setLineItems] = useState<piecesLineitem[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [editingItem, setEditingItem] = useState<piecesLineitem | null>(null)
  const [showForm, setShowForm] = useState(false)

  const initialFormData: LineitemFormData = {
    piecesLineitemId: 0,
    projectId: 0,
    quantity: 1,
    unitPrice: 0,
    xeroAccountId: 0,
    xeroSupplierId: 0,
    xeroTrackingDepartmentId: 0,
    xeroTaxRateId: 0,
    xeroTrackingTypeId: 0,
  }

  const [formData, setFormData] = useState<LineitemFormData>(initialFormData)

  // Load line items for this piece
  useEffect(() => {
    loadLineItems()
  }, [props.piecesId])

  const loadLineItems = async () => {
    if (!props.piecesId) return

    try {
      setLoading(true)
      const items = await getPiecesLineitemsByPiecesId(props.piecesId)
      console.log('Loaded line items:', items)
      console.log('Line items count:', items?.length || 0)
      setLineItems(items)
    } catch (error) {
      console.error('Error loading line items:', error)
      showToast('Error loading line items', 'error')
    } finally {
      setLoading(false)
    }
  }



  // Helper function to find dropdown item by value
  const findDropdownItem = (data: dropDown[] | undefined, value: number | undefined) => {
    if (!data || !value) return null
    return data.find(item => item.value === value) || null
  }

  // Helper function to prepare initial values for form with correct dropdown objects
  const prepareInitialValues = (data: LineitemFormData) => {
    const prepared = {
      ...data,
      projectId: findDropdownItem(props.jobsData, data.projectId),
      xeroAccountId: findDropdownItem(props.xeroAccountData, data.xeroAccountId),
      xeroTaxRateId: findDropdownItem(props.xeroTaxRateData, data.xeroTaxRateId),
      xeroTrackingDepartmentId: findDropdownItem(props.xeroDepartmentData, data.xeroTrackingDepartmentId),
      xeroSupplierId: findDropdownItem(props.xeroSupplierData, data.xeroSupplierId),
      xeroTrackingTypeId: findDropdownItem(props.xeroTypeData, data.xeroTrackingTypeId),
    }
    
    console.log('Original form data:', data)
    console.log('Prepared initial values:', prepared)
    
    return prepared
  }

  // Helper function to extract value from dropdown objects
  const getDropdownValue = (fieldValue: any): number | undefined => {
    console.log('Processing dropdown value:', fieldValue, 'Type:', typeof fieldValue)
    
    if (fieldValue === null || fieldValue === undefined) {
      return undefined
    }
    
    // If it's already a number, return it
    if (typeof fieldValue === 'number') {
      return fieldValue > 0 ? fieldValue : undefined
    }
    
    // If it's an object, try to extract value from common properties
    if (typeof fieldValue === 'object') {
      // Try 'value' property first (most common)
      if (fieldValue.value !== undefined) {
        const value = typeof fieldValue.value === 'number' ? fieldValue.value : parseInt(fieldValue.value)
        console.log('Extracted from .value:', value)
        return value > 0 ? value : undefined
      }
      
      // Try 'id' property as backup
      if (fieldValue.id !== undefined) {
        const value = typeof fieldValue.id === 'number' ? fieldValue.id : parseInt(fieldValue.id)
        console.log('Extracted from .id:', value)
        return value > 0 ? value : undefined
      }
      
      // If it's an object but no recognizable properties, log it for debugging
      console.warn('Unrecognized dropdown object structure:', fieldValue)
      return undefined
    }
    
    // Try to parse as number from string
    const parsed = parseInt(fieldValue)
    const result = !isNaN(parsed) && parsed > 0 ? parsed : undefined
    console.log('Parsed as number:', result)
    return result
  }

  // Handle form submission
    const handleSubmit = async (formValues: LineitemFormData) => {
    if (submitting) {
      console.log('Already submitting, ignoring duplicate call')
      return
    }
    
    setSubmitting(true)
    console.log('=== Form Submit Starting ===')
    console.log('Raw form values:', formValues)
    
    try {
      // Add a small delay to prevent Kendo UI conflicts
      await new Promise(resolve => setTimeout(resolve, 50))
      
      // Extract dropdown values
      const projectId = getDropdownValue(formValues.projectId)
      const xeroAccountId = getDropdownValue(formValues.xeroAccountId)
      const xeroSupplierId = getDropdownValue(formValues.xeroSupplierId)
      const xeroTrackingDepartmentId = getDropdownValue(formValues.xeroTrackingDepartmentId)
      const xeroTaxRateId = getDropdownValue(formValues.xeroTaxRateId)
      const xeroTrackingTypeId = getDropdownValue(formValues.xeroTrackingTypeId)
      
      console.log('Extracted dropdown values:', {
        projectId,
        xeroAccountId,
        xeroSupplierId,
        xeroTrackingDepartmentId,
        xeroTaxRateId,
        xeroTrackingTypeId
      })

      // Calculate net amount
      const quantity = Number(formValues.quantity) || 1
      const unitPrice = Number(formValues.unitPrice) || 0
      const netAmount = quantity * unitPrice

      // Find the tax rate to calculate tax
      const selectedTaxRate = props.xeroTaxRateData?.find(rate => rate.value === xeroTaxRateId)
      console.log('Selected tax rate:', selectedTaxRate, 'for tax rate ID:', xeroTaxRateId)
      
      // Calculate tax amount - assuming tax rate text contains the percentage (e.g., "VAT 20%")
      let taxRate = 0
      if (selectedTaxRate?.text) {
        // Try to extract percentage from text like "VAT 20%" or "20%"
        const match = selectedTaxRate.text.match(/(\d+(?:\.\d+)?)%/)
        if (match) {
          taxRate = Number(match[1]) / 100
        }
      }
      
      const taxAmount = netAmount * taxRate
      const totalAmount = netAmount + taxAmount

      console.log('Calculations:', {
        quantity,
        unitPrice,
        netAmount,
        taxRate: `${taxRate * 100}%`,
        taxAmount,
        totalAmount
      })

      // Create the payload
      const payload: CreatePiecesLineitemVM = {
        piecesId: props.piecesId,
        projectId: projectId,
        quantity: quantity,
        unitPrice: unitPrice,
        xeroAccountId: xeroAccountId,
        xeroSupplierId: xeroSupplierId,
        xeroTrackingDepartmentId: xeroTrackingDepartmentId,
        xeroTaxRateId: xeroTaxRateId,
        xeroTrackingTypeId: xeroTrackingTypeId,
        net: netAmount,
        tax: taxAmount,
        total: totalAmount,
      }

      console.log('Final payload to API:', payload)

      let result
      if (editingItem) {
        console.log('Updating existing item with ID:', editingItem.piecesLineitemId)
        result = await updatePiecesLineitem(editingItem.piecesLineitemId, payload)
      } else {
        console.log('Creating new item')
        result = await createPiecesLineitem(payload)
      }

      console.log('API Result:', result)
      showToast(editingItem ? 'Line item updated successfully' : 'Line item created successfully', 'success')
      
      // Reset form and reload data
      setFormData(initialFormData)
      setEditingItem(null)
      setShowForm(false)
      await loadLineItems()
      
    } catch (error) {
      console.error('Error in handleSubmit:', error)
      showToast(`Error ${editingItem ? 'updating' : 'creating'} line item`, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // Handle edit
  const handleEdit = (item: piecesLineitem) => {
    console.log('Editing item:', item)
    setEditingItem(item)
    setFormData({
      piecesLineitemId: item.piecesLineitemId,
      projectId: item.projectId || 0,
      quantity: item.quantity || 1,
      unitPrice: item.unitPrice || 0,
      xeroAccountId: item.xeroAccountId || 0,
      xeroSupplierId: item.xeroSupplierId || 0,
      xeroTrackingDepartmentId: item.xeroTrackingDepartmentId || 0,
      xeroTaxRateId: item.xeroTaxRateId || 0,
      xeroTrackingTypeId: item.xeroTrackingTypeId || 0,
    })
    setShowForm(true)
  }

  // Handle delete
  const handleDelete = async (item: piecesLineitem) => {
    if (!window.confirm('Are you sure you want to delete this line item?')) return

    try {
      await deletePiecesLineitem(item.piecesLineitemId)
      showToast('Line item deleted successfully', 'success')
      await loadLineItems()
    } catch (error) {
      console.error('Error deleting line item:', error)
      showToast('Error deleting line item', 'error')
    }
  }

  // Handle add new
  const handleAddNew = () => {
    setEditingItem(null)
    setFormData(initialFormData)
    setShowForm(true)
  }

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingItem(null)
    setFormData(initialFormData)
    setShowForm(false)
  }

  // Calculate totals for display
  const totals = useMemo(() => {
    return lineItems.reduce(
      (acc, item) => ({
        net: acc.net + (item.net || item.netAmount || 0),
        tax: acc.tax + (item.tax || item.taxAmount || 0),
        total: acc.total + (item.total || item.totalAmount || 0),
      }),
      { net: 0, tax: 0, total: 0 }
    )
  }, [lineItems])

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading line items...</div>
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h4>Line Items ({lineItems.length})</h4>
        {!props.lockEdit && (
          <Button themeColor="primary" onClick={handleAddNew}>
            Add Line Item
          </Button>
        )}
      </div>

      {showForm && (
        <div style={{ 
          border: '1px solid #ddd', 
          borderRadius: '4px', 
          padding: '1rem', 
          marginBottom: '1rem',
          backgroundColor: '#f9f9f9'
        }}>
          <h5>{editingItem ? 'Edit Line Item' : 'Add New Line Item'}</h5>
          <Form
            initialValues={prepareInitialValues(formData)}
            key={`form-${editingItem?.piecesLineitemId || 'new'}-${JSON.stringify(formData)}`}
            render={(formRenderProps) => {
              console.log('Form render props:', formRenderProps)
              console.log('Form valid:', formRenderProps.valid)
              console.log('Form modified:', formRenderProps.modified)
              console.log('Form errors:', formRenderProps.errors)
              return (
              <FormElement>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field
                      id="projectId"
                      name="projectId"
                      label="Project/Job"
                      component={FormDropDownList}
                      data={props.jobsData || []}
                      textField="text"
                      dataItemKey="value"
                    />
                  </div>
                  
                  <Field
                    id="xeroAccountId"
                    name="xeroAccountId"
                    label="Xero Account"
                    component={FormDropDownList}
                    data={props.xeroAccountData}
                    textField="text"
                    dataItemKey="value"
                  />

                  <Field
                    id="quantity"
                    name="quantity"
                    label="Quantity"
                    component={FormNumericTextBox}
                  />

                  <Field
                    id="unitPrice"
                    name="unitPrice"
                    label="Unit Price"
                    component={FormNumericTextBox}
                  />

                  <Field
                    id="xeroTaxRateId"
                    name="xeroTaxRateId"
                    label="Tax Rate"
                    component={FormDropDownList}
                    data={props.xeroTaxRateData}
                    textField="text"
                    dataItemKey="value"
                  />

                  <Field
                    id="xeroSupplierId"
                    name="xeroSupplierId"
                    label="Supplier"
                    component={FormDropDownList}
                    data={props.xeroSupplierData}
                    textField="text"
                    dataItemKey="value"
                  />

                  <Field
                    id="xeroTrackingDepartmentId"
                    name="xeroTrackingDepartmentId"
                    label="Department"
                    component={FormDropDownList}
                    data={props.xeroDepartmentData}
                    textField="text"
                    dataItemKey="value"
                  />

                  <Field
                    id="xeroTrackingTypeId"
                    name="xeroTrackingTypeId"
                    label="Type"
                    component={FormDropDownList}
                    data={props.xeroTypeData}
                    textField="text"
                    dataItemKey="value"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                  {(() => {
                    const quantity = formRenderProps.valueGetter('quantity') || 0
                    const unitPrice = formRenderProps.valueGetter('unitPrice') || 0
                    const netAmount = quantity * unitPrice
                    
                    // Get the selected tax rate from the form
                    const selectedTaxRateValue = getDropdownValue(formRenderProps.valueGetter('xeroTaxRateId'))
                    const selectedTaxRate = props.xeroTaxRateData?.find(rate => rate.value === selectedTaxRateValue)
                    
                    // Calculate tax rate percentage
                    let taxRate = 0
                    if (selectedTaxRate?.text) {
                      // Try to extract percentage from text like "VAT 20%" or "20%"
                      const match = selectedTaxRate.text.match(/(\d+(?:\.\d+)?)%/)
                      if (match) {
                        taxRate = Number(match[1]) / 100
                      }
                    }
                    
                    const taxAmount = netAmount * taxRate
                    const totalAmount = netAmount + taxAmount
                    
                    return (
                      <>
                        <div>
                          <label>Net Amount: £{netAmount.toFixed(2)}</label>
                        </div>
                        <div>
                          <label>Tax Amount: £{taxAmount.toFixed(2)} {selectedTaxRate?.text ? `(${selectedTaxRate.text})` : ''}</label>
                        </div>
                        <div>
                          <label>Total Amount: £{totalAmount.toFixed(2)}</label>
                        </div>
                      </>
                    )
                  })()}
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <Button 
                    themeColor="primary" 
                    disabled={submitting}
                    onClick={async (e) => {
                      if (submitting) {
                        console.log('Button clicked but submission in progress, ignoring')
                        return
                      }
                      
                      // Prevent any potential drag/drop interference
                      e.preventDefault()
                      e.stopPropagation()
                      
                      console.log('=== Manual form submit called ===')
                      console.log('FormRenderProps valid:', formRenderProps.valid)
                      console.log('FormRenderProps modified:', formRenderProps.modified)
                      
                      try {
                        // Get current form values using valueGetter
                        const currentValues = {
                          piecesLineitemId: formData.piecesLineitemId,
                          projectId: formRenderProps.valueGetter('projectId'),
                          quantity: formRenderProps.valueGetter('quantity'),
                          unitPrice: formRenderProps.valueGetter('unitPrice'),
                          xeroAccountId: formRenderProps.valueGetter('xeroAccountId'),
                          xeroSupplierId: formRenderProps.valueGetter('xeroSupplierId'),
                          xeroTrackingDepartmentId: formRenderProps.valueGetter('xeroTrackingDepartmentId'),
                          xeroTaxRateId: formRenderProps.valueGetter('xeroTaxRateId'),
                          xeroTrackingTypeId: formRenderProps.valueGetter('xeroTrackingTypeId'),
                        }
                        
                        console.log('Current form values:', currentValues)
                        await handleSubmit(currentValues)
                      } catch (error) {
                        console.error('Error in button click handler:', error)
                      }
                    }}
                  >
                    {submitting ? 'Saving...' : (editingItem ? 'Update' : 'Add')}
                  </Button>
                  <Button onClick={handleCancelEdit} disabled={submitting}>
                    Cancel
                  </Button>
                </div>
              </FormElement>
              )
            }}
          />
        </div>
      )}

      <Grid
        data={lineItems}
        style={{ height: '400px' }}
      >
        <GridColumn field="projectId" title="Job" width="120px"
          cell={({ dataItem }) => {
            const jobItem = props.jobsData?.find(job => job.value === dataItem.projectId)
            return <td>{jobItem ? jobItem.text : dataItem.projectId || '-'}</td>
          }} />
        <GridColumn field="quantity" title="Qty" width="80px" />
        <GridColumn field="unitPrice" title="Unit Price" width="100px" 
          cell={({ dataItem }) => {
            console.log('Grid item unitPrice:', dataItem.unitPrice, 'Full item:', dataItem)
            return <td>£{(dataItem.unitPrice || 0).toFixed(2)}</td>
          }} />
        <GridColumn field="netAmount" title="Net" width="100px"
          cell={({ dataItem }) => <td>£{(dataItem.netAmount || dataItem.net || 0).toFixed(2)}</td>} />
        <GridColumn field="taxAmount" title="Tax" width="100px"
          cell={({ dataItem }) => <td>£{(dataItem.taxAmount || dataItem.tax || 0).toFixed(2)}</td>} />
        <GridColumn field="totalAmount" title="Total" width="100px"
          cell={({ dataItem }) => <td>£{(dataItem.totalAmount || dataItem.total || 0).toFixed(2)}</td>} />
        {!props.lockEdit && (
          <GridColumn title="Actions" width="100px"
            cell={({ dataItem }) => (
              <td>
                <Button 
                  size="small" 
                  onClick={() => handleEdit(dataItem)} 
                  style={{ marginRight: '0.5rem' }}
                  title="Edit"
                  icon="edit"
                />
                <Button 
                  size="small" 
                  fillMode="outline" 
                  onClick={() => handleDelete(dataItem)}
                  title="Delete"
                  icon="delete"
                  themeColor="error"
                />
              </td>
            )}
          />
        )}
      </Grid>

      {lineItems.length > 0 && (
        <div style={{ 
          marginTop: '1rem', 
          padding: '1rem', 
          backgroundColor: '#f9f9f9', 
          borderRadius: '4px',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <strong>Totals:</strong>
          <div>
            <span style={{ marginRight: '2rem' }}>Net: £{totals.net.toFixed(2)}</span>
            <span style={{ marginRight: '2rem' }}>Tax: £{totals.tax.toFixed(2)}</span>
            <span>Total: £{totals.total.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default PiecesLineitemManager